import { useCallback, useEffect, useState } from 'react';
import { ApiError } from '../../services/api';

/** The server's own message when it sent one, otherwise the caller's wording. */
export const describeError = (err: unknown, fallback: string): string =>
  err instanceof ApiError && err.message ? err.message : fallback;

export type AdminFetchState<T> =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | { kind: 'loaded'; data: T };

interface AdminFetchMessages {
  /** Prefix for the console error, e.g. "Failed to load prospect sources:". */
  log: string;
  /** Shown when the server gave no message of its own. */
  fallback: string;
}

/**
 * One admin read keyed by an id, with a retry.
 *
 * - Changing `key` aborts the previous request, so a slow answer for the last key never lands on
 *   this one.
 * - A retry leaves the error (and its focused Try again button) up until the answer arrives, rather
 *   than unmounting the button under the keyboard. Callers that show different keys in one place
 *   should key the consuming component per id, so an error never carries over.
 *
 * `load` must be stable across renders (a module-level service function), or every render refetches.
 */
export const useAdminFetch = <T>(
  key: string,
  load: (key: string, signal: AbortSignal) => Promise<T>,
  { log, fallback }: AdminFetchMessages
) => {
  const [state, setState] = useState<AdminFetchState<T>>({ kind: 'loading' });
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    setState((current) => (current.kind === 'error' ? current : { kind: 'loading' }));

    load(key, controller.signal)
      .then((data) => {
        if (controller.signal.aborted) return;
        setState({ kind: 'loaded', data });
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        console.error(log, err);
        setState({ kind: 'error', message: describeError(err, fallback) });
      });

    return () => controller.abort();
  }, [key, load, log, fallback, reloadToken]);

  const reload = useCallback(() => setReloadToken((value) => value + 1), []);

  return { state, reload };
};
